import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Copy } from 'lucide-react';
import { CustomRecipe } from '@/types/custom-recipe';
import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { formatMacro } from '@/utils/gram-ml';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { TagOverflowTooltip } from '@/components/recipes/TagOverflowTooltip';
import { Tag } from '@/components/ui/tag';
import recipeClockIcon from '@/assets/images/recipes/recipe-clock-icon.svg';
import recipeServingsIcon from '@/assets/images/recipes/recipe-servings-icon.svg';
import recipeIngredientsIcon from '@/assets/images/recipes/recipe-ingredients-icon.svg';
import deleteIcon from '@/assets/images/trash-bin-trash.svg';

const ACCENT = '#2EB4E4';
const METRIC_ACCENT = '#20AEDC';

interface RecipeCardProps {
  recipe: CustomRecipe;
  onView?: (recipe: CustomRecipe) => void;
  onEdit?: (recipe: CustomRecipe) => void;
  onDelete?: (recipe: CustomRecipe) => void;
  onCopy?: (recipe: CustomRecipe) => void;
  onAddToMealPlan?: (recipe: CustomRecipe) => void;
  showActions?: boolean;
  canEditDelete?: boolean;
  showCopyAction?: boolean;
  createdByLabel?: string;
  isDeleting?: boolean;
  isAnyDeleting?: boolean;
  isCopying?: boolean;
  variant?: 'recipe' | 'template';
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
}

type RecipeImageFallbacks = CustomRecipe & {
  imageUrl?: string;
  recipeImageUrl?: string;
};

function formatDuration(minutes: number): string {
  if (!minutes || minutes <= 0) return '—';
  if (minutes < 60) return `${minutes} Mins`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  onView,
  onEdit,
  onDelete,
  onCopy,
  onAddToMealPlan,
  showActions = true,
  canEditDelete = true,
  showCopyAction = false,
  createdByLabel,
  isDeleting = false,
  isAnyDeleting = false,
  isCopying = false,
  variant = 'recipe',
  primaryActionLabel,
  secondaryActionLabel,
}) => {
  const servings = Math.max(1, recipe.servings || 1);
  const per = (total: number) => total / servings;
  const recipeImages = recipe as RecipeImageFallbacks;
  const thumbnail = recipeImages.image?.trim() || recipeImages.imageUrl?.trim() || recipeImages.recipeImageUrl?.trim() || DEFAULT_RECIPE_IMAGE;
  const visibleDietLabels = recipe.dietLabels?.slice(0, 2) ?? [];
  const hiddenDietLabels = (recipe.dietLabels ?? [])
    .slice(visibleDietLabels.length)
    .map((label) => getDietaryDisplayName(label, 'dietLabels'));

  const handleView = () => {
    if (onView) onView(recipe);
  };

  const stopActionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation?.();
  };

  const handleEdit = (e: React.MouseEvent) => {
    stopActionClick(e);
    onEdit?.(recipe);
  };

  const handleDelete = (e: React.MouseEvent) => {
    stopActionClick(e);
    onDelete?.(recipe);
  };

  const handleCopy = (e: React.MouseEvent) => {
    stopActionClick(e);
    onCopy?.(recipe);
  };

  const handleViewClick = (e: React.MouseEvent) => {
    stopActionClick(e);
    handleView();
  };

  if (variant === 'template') {
    return (
      <Card
        data-testid="recipe-card"
        className="group flex h-full w-full cursor-pointer flex-col rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md"
        style={{ borderTop: '1.84px solid #E5E7EB' }}
        onClick={handleView}
      >
        <div className="mb-3.5 flex items-start gap-3">
          <img src={thumbnail} alt="" className="h-14 w-14 shrink-0 rounded-full border border-slate-200 object-cover" />
          <h3 className="line-clamp-2 pt-1 text-[21px] font-medium leading-[1.35] tracking-[-0.02em] text-[#1E2939]">{recipe.title}</h3>
        </div>

        <p className="mb-4 min-h-[36px] text-xs leading-relaxed text-[#4A5565]">
          {recipe.description?.trim() || 'No description available for this template.'}
        </p>

        <RecipeAllergenCard
          allergens={recipe.allergens}
          compact
          maxVisible={3}
          className="mb-3"
        />

        <div className="mb-3 flex items-center justify-between border-y border-[#E3E3E369] py-3">
          <span className="text-[31px] font-bold leading-none text-[#20AEDC]">{formatMacro(per(recipe.totalCalories || 0))} kcal</span>
          <div className="flex items-center gap-1.5">
            {visibleDietLabels.map((label, idx) => (
              <Tag key={idx} variant="info" size="sm" className="text-[#0A0A0A]">
                {getDietaryDisplayName(label, 'dietLabels')}
              </Tag>
            ))}
          </div>
        </div>

        <div className="mb-3 flex items-center justify-between border-b border-[#E3E3E369] pb-3">
          <div className="text-xs text-[#4A5565]">Protein <span className="font-bold text-[#374151]">{formatMacro(per(recipe.totalProtein || 0))}g</span></div>
          <div className="text-xs text-[#4A5565]">Carbs <span className="font-bold text-[#374151]">{formatMacro(per(recipe.totalCarbs || 0))}g</span></div>
          <div className="text-xs text-[#4A5565]">Fat <span className="font-bold text-[#374151]">{formatMacro(per(recipe.totalFat || 0))}g</span></div>
          <div className="text-xs text-[#4A5565]">Fiber <span className="font-bold text-[#374151]">{formatMacro(per(recipe.totalFiber || 0))}g</span></div>
        </div>

        <div className="mt-auto grid grid-cols-2 gap-2">
          <Button type="button" className="h-10 rounded-lg border-0 text-[14px] font-medium text-white shadow-none hover:opacity-90 transition-opacity" style={{ backgroundColor: ACCENT }} onClick={handleViewClick}>
            {primaryActionLabel || 'View Recipe'}
          </Button>
          <Button type="button" variant="outline" className="h-10 rounded-lg border border-slate-200 bg-white text-[14px] font-medium text-slate-500 hover:bg-slate-50 transition-colors" onClick={handleEdit}>
            {secondaryActionLabel || 'Edit Recipe'}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      data-testid="recipe-card"
      className="group flex h-full w-full cursor-pointer flex-col rounded-xl border border-[#E5E7EB] bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md"
      style={{ borderTop: '1.84px solid #E5E7EB' }}
      onClick={handleView}
    >
      {/* Header */}
      <div className="mb-4 flex items-start gap-3">
        <img src={thumbnail} alt="" className="h-14 w-14 shrink-0 rounded-full border border-slate-200 object-cover" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-slate-900">{recipe.title}</h3>
            <div className="flex shrink-0 items-center gap-4">
              {recipe.isPublic ? (
                <Badge className="h-[22px] rounded-lg border-0 px-5 text-xs font-bold text-white" style={{ backgroundColor: METRIC_ACCENT }}>Public</Badge>
              ) : (
                <Badge className="h-[22px] rounded-lg border-0 bg-[#00C28C] px-5 text-xs font-bold text-white hover:bg-[#00C28C]">Private</Badge>
              )}
              {canEditDelete && (
                <button type="button" className="text-red-400 hover:text-red-600 transition-colors" onClick={handleDelete} disabled={isDeleting || isAnyDeleting} aria-label={`Delete ${recipe.title}`}>
                  {isDeleting ? <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <img src={deleteIcon} alt="" className="block h-6 w-6" />}
                </button>
              )}
              {!canEditDelete && showCopyAction && !recipe.isPublic && recipe.canCopy !== false && (
                <button type="button" className="text-amber-500 hover:text-amber-700 transition-colors" onClick={handleCopy} disabled={isCopying} aria-label={`Copy ${recipe.title}`}>
                  {isCopying ? (
                    <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : (
                    <Copy className="block h-6 w-6" aria-hidden />
                  )}
                </button>
              )}
            </div>
          </div>
          <p className="mt-1 text-xs text-slate-500">Created by: {createdByLabel || 'You'}</p>
        </div>
      </div>

      {showCopyAction && recipe.canCopy === false && (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-4 text-amber-800"
          onClick={stopActionClick}
          role="note"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{recipe.copyUnavailableReason || 'Legacy recipe: copying is unavailable because an ingredient is no longer linked to the catalog.'}</span>
        </div>
      )}

      {/* Stats row */}
      <div className="mb-4 flex items-center gap-[1.6rem] text-[13px]">
        <div className="flex items-center gap-1.5">
          <img src={recipeClockIcon} alt="" className="h-[13px] w-[13px] shrink-0" />
          <span className="font-semibold" style={{ color: METRIC_ACCENT }}>{formatDuration(recipe.totalTimeMinutes || recipe.readyInMinutes || 0)}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <img src={recipeServingsIcon} alt="" className="h-[13px] w-[13px] shrink-0" />
          <span className="font-semibold" style={{ color: METRIC_ACCENT }}>{recipe.servings} {recipe.servings > 1 ? 'Servings' : 'Serving'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <img src={recipeIngredientsIcon} alt="" className="h-[13px] w-[13px] shrink-0" />
          <span className="font-semibold" style={{ color: METRIC_ACCENT }}>{recipe.ingredientCount ?? 0} Ingredients</span>
        </div>
      </div>

      {/* Description */}
      <p className="mb-5 line-clamp-2 flex-1 text-xs leading-relaxed text-[#4A5565]">
        {recipe.description?.trim() || 'No description available for this recipe.'}
      </p>

      <RecipeAllergenCard
        allergens={recipe.allergens}
        compact
        maxVisible={3}
        className="mb-4"
      />

      {/* Calories & Badges */}
      <div className="mb-4 flex flex-nowrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-baseline gap-1 leading-none">
          <span className="text-[22px] font-bold" style={{ color: METRIC_ACCENT }}>{formatMacro(per(recipe.totalCalories || 0))}</span>
          <span className="text-[15px] font-bold" style={{ color: METRIC_ACCENT }}>kcal</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {visibleDietLabels.length > 0 ? (
            visibleDietLabels.map((label, idx) => (
              <Tag key={idx} variant="info" size="sm" className="px-3 py-[5px] text-[12px] text-slate-700">
                {getDietaryDisplayName(label, 'dietLabels')}
              </Tag>
            ))
          ) : null}
          <TagOverflowTooltip
            label="diet labels"
            values={hiddenDietLabels}
            className="min-h-0 rounded-full border-0 bg-[#EFF6FF] px-3 py-[5px] text-[12px] font-semibold text-slate-700"
            itemClassName="border-blue-100 bg-blue-50 text-slate-700"
            showHeading={false}
          />
        </div>
      </div>

      <div className="mb-2 h-px w-full bg-slate-100" />

      {/* Macros */}
      <div className="flex items-center justify-between px-0.5">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[12px] text-slate-500">Protein</span>
          <span className="text-[13px] font-semibold text-slate-800">{formatMacro(per(recipe.totalProtein || 0))}g</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[12px] text-slate-500">Carbs</span>
          <span className="text-[13px] font-semibold text-slate-800">{formatMacro(per(recipe.totalCarbs || 0))}g</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[12px] text-slate-500">Fat</span>
          <span className="text-[13px] font-semibold text-slate-800">{formatMacro(per(recipe.totalFat || 0))}g</span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-[12px] text-slate-500">Fibre</span>
          <span className="text-[13px] font-semibold text-slate-800">{formatMacro(per(recipe.totalFiber || 0))}g</span>
        </div>
      </div>

      <div className="mt-2 mb-2.5 h-px w-full bg-slate-100" />

      {/* Actions */}
      {showActions && (
        <div className={`mt-auto grid gap-3 pt-1 ${canEditDelete ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <Button type="button" className="h-[38px] rounded-lg border-0 text-[13px] font-semibold text-white shadow-none hover:opacity-90 transition-opacity" style={{ backgroundColor: ACCENT }} onClick={handleViewClick}>
            View Recipe
          </Button>
          {canEditDelete && (
            <Button type="button" variant="outline" className="h-[38px] rounded-lg border border-slate-200 bg-white text-[13px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors" onClick={handleEdit}>
              Edit Recipe
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};
